/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import './ValueList.scss';
import { getOption, initCustomOptions, mergeCustomOptions } from './selectUtils';
import { InputProps } from './InputProps';
import { getAddonSingleElement } from './addonUtils';
import { Option } from '@g17eco/molecules/select/SelectFactory';
import { SelectComponent } from './SelectComponent';
import { ValueData } from '@g17eco/types/universalTrackerValue';
import { normalizeText } from '@utils/string-format';

const getSafeValueData = (valueDataData: ValueData['data']): string[] => {
  if (Array.isArray(valueDataData)) {
    return valueDataData.map((v) => ('string' === typeof v ? v : ''));
  }
  const newValueData: string[] = [];
  if ('string' === typeof valueDataData) {
    newValueData.push(valueDataData);
  }
  return newValueData;
};

export interface ValueListProps
  extends Pick<
    InputProps,
    | 'isDisabled'
    | 'saving'
    | 'universalTracker'
    | 'valueDataData'
    | 'handleValueDataChange'
    | 'placeholder'
    | 'addons'
    | 'isReadOnlyOptions'
    | 'onSaveCustomOption'
    | 'subsidiaryUtr'
  > {
  multi?: boolean;
}

export default function ValueList(props: ValueListProps) {
  const {
    isDisabled,
    saving,
    universalTracker,
    valueDataData,
    handleValueDataChange,
    placeholder,
    addons,
    isReadOnlyOptions,
    onSaveCustomOption,
    subsidiaryUtr,
  } = props;

  const valueValidation = universalTracker.getValueValidation();
  const allowCustomOptions = !!valueValidation.valueList?.allowCustomOptions;
  const isMultipleChoice = props.multi === true;

  const disabled = isDisabled?.(props);
  const data = useMemo(() => getSafeValueData(valueDataData), [valueDataData]);

  const [options, setOptions] = useState<Option<string>[]>([]);

  useEffect(() => {
    // deals with initial options set and custom options in one go
    const baseValueListOptions = universalTracker.getValueListOptions();

    const mergedValueListOptions = mergeCustomOptions(
      baseValueListOptions,
      subsidiaryUtr?.valueListConfig?.options,
    );

    const baseOptions = mergedValueListOptions.reduce((acc, cur) => {
      const isCurrentValue = data.includes(cur.code);

      if (!cur.hidden || isCurrentValue) {
        acc.push({ value: cur.code, label: cur.name });
      }
      return acc;
    }, [] as Option<string>[]);

    // deals with adding custom option from column value on edit
    if (!data.length || !baseOptions.length) {
      setOptions(baseOptions);
      return;
    }

    const selectOptionValues = baseOptions.map((op) => normalizeText(String(op.value)));
    const isSelectedValueExisting = data.every((value) => selectOptionValues.includes(normalizeText(String(value))));

    if (isSelectedValueExisting) {
      setOptions(baseOptions);
      return;
    }

    const finalOptions = initCustomOptions(data, baseOptions, allowCustomOptions);
    setOptions(finalOptions);
  }, [universalTracker, data, allowCustomOptions, subsidiaryUtr]);

  const selectedOptions: Option<string>[] = useMemo(() => {
    return data.reduce((acc, value) => {
      const option = getOption(options, normalizeText(String(value)), allowCustomOptions);
      if (!option.isDisabled) {
        acc.push(option);
      }
      return acc;
    }, [] as Option<string>[]);
  }, [allowCustomOptions, data, options]);

  const { beforeElement, afterElement } = getAddonSingleElement(addons);

  const tooManySelections = useMemo(() => {
    if (!isMultipleChoice || valueValidation.max === undefined) {
      // Ignore for single choice, or if no max is set or nothing selected
      return false;
    }
    return selectedOptions.length >= valueValidation.max;
  }, [isMultipleChoice, valueValidation, selectedOptions]);

  const selectOptions = options.map((op) => ({
    ...op,
    searchString: `${op.label} ${op.value}`,
    isDisabled: (tooManySelections && !selectedOptions.some((v) => v.value === op.value)) || isReadOnlyOptions,
  }));

  return (
    <div className='position-relative input-group'>
      {beforeElement || null}
      <SelectComponent
        placeholder={placeholder}
        isDisabled={disabled || saving}
        options={selectOptions}
        setOptions={setOptions}
        value={selectedOptions}
        allowCustomOptions={allowCustomOptions}
        isMulti={isMultipleChoice}
        handleChange={handleValueDataChange}
        limit={valueValidation.max}
        isReadOnlyOptions={isReadOnlyOptions}
        onSaveCustomOption={onSaveCustomOption}
      />
      {afterElement || null}
    </div>
  );
}
